import { Map } from "immutable"
import { push } from "react-router-redux"

import {
    STUDIO_CLONE_MAP,
    STUDIO_DIMENSION_ITEM_REQUEST,
    STUDIO_HIGHLIGHT_DIMENSION,
    STUDIO_HIGHLIGHT_DIMENSION_NEXT,
    STUDIO_HIGHLIGHT_DIMENSION_PREVIOUS,
    STUDIO_HIGHLIGHT_TOPIC,
    STUDIO_ITEM_REQUEST,
    STUDIO_ITEM_RESPONSE,
    STUDIO_KNOWLEDGE_ITEM_REQUEST,
    STUDIO_KNOWLEDGE_LIST_REQUEST,
    STUDIO_LIST_REQUEST,
    STUDIO_LIST_SEARCH_REQUEST,
    STUDIO_LIST_TYPE_CHANGE,
    STUDIO_PROJECTS_LIST_REQUEST,
    STUDIO_REPORTS_LIST_REQUEST,
    STUDIO_KNOWLEDGE_DATA_LIST_REQUEST,
    STUDIO_SESSIONS_LIST_REQUEST,
    STUDIO_STAKEHOLDERS_LIST_REQUEST
} from "../actions/action-types"
import { dimensionItemRequest } from "../actions/dimension-actions"
import {
    enrichOwnersViewers,
    hydrate as hydrateMap,
    normalisingFields
} from "../models/entities/map"
import { getMapList, getMapItem } from "../api/map-api"
import {
    knowledgeListRequest,
    knowledgeItemRequest
} from "../actions/knowledge-actions"
import { projectListRequest } from "../actions/project-actions"
import { reportListRequest } from "../actions/report-actions"
import { knowledgeDataListRequest } from "../actions/knowledge-data-actions"
import {
    requestListEpic,
    requestItemHandler,
    requestListSearchEpic,
    requestListFilterEpic
} from "./default-epics/default-entity-epics"
import { sessionListRequest } from "../actions/session-actions"
import { stakeholderListRequest } from "../actions/stakeholder-actions"
import {
    studioListRequest,
    studioListCachedResponse,
    studioListResponse,
    studioListErrorResponse,
    studioItemResponse,
    studioItemCachedResponse,
    studioItemErrorResponse,
    studioHighlightDimension,
    studioDimensionItemResponse,
    studioKnowledgeItemResponse
} from "../actions/studio-actions"

export const requestStudioListEpic = requestListEpic(
    STUDIO_LIST_REQUEST,
    getMapList,
    "maps",
    studioListResponse,
    studioListCachedResponse,
    studioListErrorResponse
)

export const requestStudioListSearchEpic = requestListSearchEpic(
    STUDIO_LIST_SEARCH_REQUEST,
    studioListRequest,
    "studio",
    "maps"
)

export const studioListTypeChangeEpic = requestListFilterEpic(
    STUDIO_LIST_TYPE_CHANGE,
    studioListRequest,
    "studio",
    "maps"
)

export const requestStudioItemEpic = (action$, { getState }) =>
    action$
        .ofType(STUDIO_ITEM_REQUEST)
        .mergeMap(
            requestItemHandler(
                getState,
                getMapItem,
                "maps",
                hydrateMap,
                normalisingFields,
                studioItemResponse,
                studioItemCachedResponse,
                studioItemErrorResponse
            )
        )
        .map(action => {
            if (action.type === STUDIO_ITEM_RESPONSE)
                action.data = enrichOwnersViewers(action.data)
            return action
        })

export const studioDimensionHighlightNextEpic = (action$, { getState }) =>
    action$
        .ofType(STUDIO_HIGHLIGHT_DIMENSION_NEXT)
        .filter(() => getState().presentation.studio.highlightedId)
        .map(() => {
            const {
                highlightedId,
                workingModel
            } = getState().presentation.studio
            const dimensions = workingModel.dimensions
            const newHighlightIndex =
                (dimensions.findIndex(({ id }) => highlightedId === id) + 1) %
                dimensions.length
            return studioHighlightDimension(dimensions[newHighlightIndex].id)
        })

export const studioDimensionHighlightPreviousEpic = (action$, { getState }) =>
    action$
        .ofType(STUDIO_HIGHLIGHT_DIMENSION_PREVIOUS)
        .filter(() => getState().presentation.studio.highlightedId)
        .map(() => {
            const {
                highlightedId,
                workingModel
            } = getState().presentation.studio
            const dimensions = workingModel.dimensions
            const highlightedIndex = dimensions.findIndex(
                ({ id }) => highlightedId === id
            )
            const newHighlightIndex =
                (highlightedIndex === 0
                    ? dimensions.length
                    : highlightedIndex) - 1
            return studioHighlightDimension(dimensions[newHighlightIndex].id)
        })

export const studioTopicHighlightEpic = (action$, { getState }) =>
    action$
        .ofType(STUDIO_HIGHLIGHT_TOPIC, STUDIO_HIGHLIGHT_DIMENSION)
        .map(({ type, id }) => {
            const state = getState()
            const { activeId } = state.presentation.studio
            const pathComponents = getPathComponents()
            const isValid = isPathValidForHighlighting(pathComponents, activeId)
            const potentialTab =
                pathComponents.length > 0 &&
                pathComponents[pathComponents.length - 1]
            const tab = isValidTab(potentialTab) ? "/" + potentialTab : "/feed"
            const returnValue =
                isValid &&
                ((type === STUDIO_HIGHLIGHT_TOPIC &&
                    `/studio/${activeId}/view${tab}`) ||
                    `/studio/${activeId}/view/${id}${tab}`)
            return returnValue
        })
        .filter(route => route && window.location.pathname !== route)
        .map(route => push(route))

function isValidTab(potentialTab) {
    return (
        potentialTab &&
        [
            "summary",
            "feed",
            "stakeholders",
            "sessions",
            "projects",
            "reports",
            "data"
        ].indexOf(potentialTab) !== -1
    )
}

function isPathValidForHighlighting(pathComponents, activeId) {
    return (
        (pathComponents.length > 0 &&
            pathComponents[0] !== "new" &&
            pathComponents[0] !== activeId) ||
        (pathComponents.length > 1 &&
            pathComponents[1] !== "edit" &&
            pathComponents[1] !== "create-key-issue")
    )
}

function getPathComponents() {
    return window.location.pathname
        .split("/")
        .filter(component => component !== "" && component !== "studio")
}

export const studioCloneMapEpic = (action$, { getState }) =>
    action$
        .ofType(STUDIO_CLONE_MAP)
        .map(() =>
            push(`/studio/${getState().presentation.studio.activeId}/edit`)
        )

export const studioDimensionItemRequestEpic = action$ =>
    action$
        .ofType(STUDIO_DIMENSION_ITEM_REQUEST)
        .map(({ id, isInvalidating }) =>
            dimensionItemRequest(
                id,
                isInvalidating,
                studioDimensionItemResponse
            )
        )

export const studioKnowledgeListRequestEpic = action$ =>
    action$
        .ofType(STUDIO_KNOWLEDGE_LIST_REQUEST)
        .filter(({ highlightedId }) => highlightedId)
        .map(
            ({
                type,
                isViewingTopic,
                highlightedId,
                publicationType,
                ...pagingRequest
            }) => {
                let searchCriteria =
                    pagingRequest.searchCriteria ||
                    Map().set("order", "time").set("approved", true)
                searchCriteria = switchBetweenTopicAndDimension(
                    searchCriteria,
                    isViewingTopic,
                    highlightedId,
                    "topics",
                    "dimensions"
                )
                if (publicationType) {
                    searchCriteria = searchCriteria.set(
                        "source_publication_type",
                        publicationType
                    )
                } else {
                    searchCriteria = searchCriteria.remove(
                        "source_publication_type"
                    )
                }
                return knowledgeListRequest({
                    ...pagingRequest,
                    searchCriteria
                })
            }
        )

export const studioKnowledgeItemRequestEpic = action$ =>
    action$
        .ofType(STUDIO_KNOWLEDGE_ITEM_REQUEST)
        .map(({ id, isInvalidating }) =>
            knowledgeItemRequest(
                id,
                isInvalidating,
                studioKnowledgeItemResponse
            )
        )

export const studioStakeholderListRequestEpic = (action$, { getState }) =>
    action$
        .ofType(STUDIO_STAKEHOLDERS_LIST_REQUEST)
        .filter(({ highlightedId }) => highlightedId)
        .map(
            ({
                type,
                isViewingTopic,
                highlightedId,
                filteredRole,
                ...pagingRequest
            }) => {
                let searchCriteria = pagingRequest.searchCriteria || Map()
                searchCriteria = switchBetweenTopicAndDimension(
                    searchCriteria,
                    isViewingTopic,
                    highlightedId
                ).set("approved", true)
                if (isViewingTopic)
                    searchCriteria = searchCriteria.remove("context")
                else
                    searchCriteria = searchCriteria.set(
                        "context",
                        getState().presentation.studio.activeId
                    )

                if (filteredRole) {
                    searchCriteria = searchCriteria.set("role", filteredRole)
                } else {
                    searchCriteria = searchCriteria.remove("role")
                }

                return stakeholderListRequest({
                    ...pagingRequest,
                    searchCriteria
                })
            }
        )

export const studioSessionListRequestEpic = action$ =>
    action$
        .ofType(STUDIO_SESSIONS_LIST_REQUEST)
        .filter(({ highlightedId }) => highlightedId)
        .map(getRelatedDataRequest(sessionListRequest))

export const studioProjectListRequestEpic = action$ =>
    action$
        .ofType(STUDIO_PROJECTS_LIST_REQUEST)
        .filter(({ highlightedId }) => highlightedId)
        .map(getRelatedDataRequest(projectListRequest))

const defaultReportListCreator = getRelatedDataRequest(
    reportListRequest,
    "topic",
    "dimension"
)
export const studioReportListRequestEpic = action$ =>
    action$
        .ofType(STUDIO_REPORTS_LIST_REQUEST)
        .filter(({ highlightedId }) => highlightedId)
        .map(action => {
            const defaultReportList = defaultReportListCreator(action)
            const newSearchCriteria = defaultReportList.searchCriteria.set(
                "group_by",
                "publisher"
            )
            return {
                ...defaultReportList,
                searchCriteria: newSearchCriteria
            }
        })

export const studioKnowledgeDataListRequestEpic = action$ =>
    action$
        .ofType(STUDIO_KNOWLEDGE_DATA_LIST_REQUEST)
        .filter(({ highlightedId }) => highlightedId)
        .map(
            getRelatedDataRequest(
                knowledgeDataListRequest,
                "topics",
                "dimensions"
            )
        )

function getRelatedDataRequest(
    actionCreator,
    topicName = "topic",
    dimensionName = "dimension"
) {
    return ({ type, isViewingTopic, highlightedId, ...pagingRequest }) => {
        let searchCriteria = pagingRequest.searchCriteria || Map()
        searchCriteria = switchBetweenTopicAndDimension(
            searchCriteria,
            isViewingTopic,
            highlightedId,
            topicName,
            dimensionName
        )
        return actionCreator({
            ...pagingRequest,
            searchCriteria
        })
    }
}

function switchBetweenTopicAndDimension(
    searchCriteria,
    isViewingTopic,
    highlightedId,
    topicName = "topic",
    dimensionName = "dimension"
) {
    return searchCriteria
        .set(isViewingTopic ? topicName : dimensionName, highlightedId)
        .remove(isViewingTopic ? dimensionName : topicName)
}
